import { useEffect, useState } from "react";
import type { ApprovalMode, HierarchyTier, OrgRolesDto, OrgSettingsInput, TierLevels } from "@gatiq/shared";
import { chainToSettings, defaultRoleName, levelsOf } from "@gatiq/shared";
import { Button, Card, CardBody, CardHeader, PageBody, Skeleton, Switch, TextInput, useToast } from "@/ui";
import { TabBarPortal } from "@/layout/PageChrome";
import { api, ApiClientError } from "@/lib/api";
import { chosenRoleNames, HierarchyDragDropBuilder } from "@/components/HierarchyDragDropBuilder";
import { useLanguage } from "@/i18n/language";
import { invalidateOrgRoles } from "@/auth/useOrgRoles";

interface SettingsResponse {
  karigarApprovalMode: ApprovalMode;
  /** D-20: the super admin's key, and the admin's own switch. */
  dispatchAvailable?: boolean;
  dispatchEnabled?: boolean;
  distributorEnabled: boolean;
  enabledTiers?: HierarchyTier[];
  tierLevels?: TierLevels;
  commissionSettings?: {
    counterCommissionPoints?: number;
    distributorCommissionPoints?: number;
  };
  commission?: {
    counterFixedPoints?: number;
    distributorFixedPoints?: number;
    distributorLevelPoints?: number[];
  };
}

/**
 * The General tab of an org's Settings — the distribution chain and what each
 * step earns per scan. Support phone and email are branding tokens and are
 * edited on the Contact details tab.
 *
 * A panel, not a page: `SettingsLayout` owns the `<Page>` and the heading.
 */
export function OrgGeneralSettingsPanel() {
  const toast = useToast();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // The chain top to bottom, copies included: ["sales_rep:1", "distributor:1", "sales_rep:2", …].
  const [chain, setChain] = useState<string[]>(["sales_rep:1", "counter", "karigar"]);
  const [counterPoints, setCounterPoints] = useState<number>(10);
  // Only names the org chose; a blank field means the default ("Distributor 2").
  const [roleNames, setRoleNames] = useState<Record<string, string>>({});
  const [dispatchAvailable, setDispatchAvailable] = useState(false);
  const [dispatchEnabled, setDispatchEnabled] = useState(false);
  const [savingDispatch, setSavingDispatch] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get<SettingsResponse>("/org/settings"), api.get<OrgRolesDto>("/org/roles")])
      .then(([settings, roles]) => {
        if (cancelled) return;
        setRoleNames(
          Object.fromEntries(
            roles.roles
              .filter((r) => r.name !== defaultRoleName(r.base, r.level, levelsOf(roles.tierLevels, r.base)))
              .map((r) => [r.key, r.name]),
          ),
        );
        setChain(roles.chain);
        setDispatchAvailable(Boolean(settings.dispatchAvailable));
        setDispatchEnabled(Boolean(settings.dispatchEnabled));

        const cp =
          settings.commissionSettings?.counterCommissionPoints ??
          settings.commission?.counterFixedPoints ??
          10;
        setCounterPoints(cp);
      })
      .catch((err: unknown) => {
        toast.error(err instanceof ApiClientError ? err.message : "Could not load settings.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const { enabledTiers, tierLevels, chainOrder } = chainToSettings(chain);

  // Saved the moment it is flipped: it changes menus and printing for everyone.
  async function toggleDispatch(next: boolean): Promise<void> {
    setSavingDispatch(true);
    try {
      const res = await api.patch<SettingsResponse>("/org/settings", { dispatchEnabled: next });
      setDispatchEnabled(Boolean(res.dispatchEnabled));
      invalidateOrgRoles();
      toast.success(next ? "Dispatch turned on." : "Dispatch turned off.");
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not change dispatch.");
    } finally {
      setSavingDispatch(false);
    }
  }

  async function save(): Promise<void> {
    setSaving(true);
    try {
      const isDistributorActive = enabledTiers.includes("distributor");
      const input: OrgSettingsInput = {
        distributorEnabled: isDistributorActive,
        enabledTiers,
        tierLevels,
        chainOrder,
        roleNames: chosenRoleNames(roleNames),
        commission: {
          counterFixedPoints: counterPoints,
          distributorFixedPoints: 0,
          distributorLevelPoints: [],
        },
      };
      await api.patch<SettingsResponse>("/org/settings", input);

      // The sidebar and the team pages read tiers and names from the role list.
      invalidateOrgRoles();

      toast.success("Settings saved.");
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not save settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {/* Same place as the Branding tab's Save: the end of the tab row. */}
      <TabBarPortal>
        <Button loading={saving} onClick={() => void save()} size="sm">
          Save
        </Button>
      </TabBarPortal>

      <PageBody>
      {loading ? (
        <Card>
          <CardBody className="flex flex-col gap-3">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </CardBody>
        </Card>
      ) : (
        <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <HierarchyDragDropBuilder
            names={roleNames}
            onChange={setChain}
            onNamesChange={setRoleNames}
            value={chain}
          />

          <div className="flex flex-col gap-5 xl:sticky xl:top-0">
          <Card>
            <CardHeader title={t("प्रति स्कैन कमीशन", "Commission per scan")} />
            <CardBody className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
              <TextInput
                label="Counter earns (QCoin)"
                min={0}
                onChange={(e) => setCounterPoints(Number(e.target.value) || 0)}
                type="number"
                value={String(counterPoints)}
              />
              <p className="text-xs text-muted">Only counters and Karigars earn QCoin. Sales reps and distributors manage the network and stock.</p>
            </CardBody>
          </Card>

          {dispatchAvailable ? (
            <Card>
              <CardHeader
                action={
                  <Switch
                    checked={dispatchEnabled}
                    disabled={savingDispatch}
                    label="Dispatch and stock"
                    onChange={(next) => void toggleDispatch(next)}
                  />
                }
                title={t("डिस्पैच और स्टॉक", "Dispatch and stock")}
              />
              <CardBody className="text-sm text-muted">
                {dispatchEnabled
                  ? "Batches printed now are numbered and tracked. Karigars can scan them once they reach a shop."
                  : "Off. Scanning and commission work as before."}
              </CardBody>
            </Card>
          ) : null}
          </div>
        </div>
      )}
      </PageBody>
    </>
  );
}
